import React, { useState, useEffect } from "react";
import { FormFooter } from "@/components/FormFooter";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { type TipoMovimiento } from "@/lib/api";
import {
  coerceUsdIngreso,
  type MonedaCaja,
} from "@/lib/usdSoloIngreso";

interface MovimientosABMProps {
  tipo: TipoMovimiento | null;
  onBack: () => void;
  onSave: (tipo: TipoMovimiento) => void;
  onDelete: (codigo: string) => void;
}

export function MovimientosABM({ tipo, onBack, onSave, onDelete }: MovimientosABMProps) {
  const [codigo, setCodigo] = useState("");
  const [nombre, setNombre] = useState("");
  const [descripcion, setDescripcion] = useState("");
  const [fondo, setFondo] = useState(false);
  const [tipoFondo, setTipoFondo] = useState<"Ingreso" | "Egreso">("Egreso");
  const [moneda, setMoneda] = useState<MonedaCaja>("ARS");
  const [asociarUsuario, setAsociarUsuario] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    if (tipo) {
      setCodigo(tipo.codigo);
      setNombre(tipo.nombre);
      setDescripcion(tipo.descripcion || "");
      setFondo(Boolean(tipo.fondo));
      setTipoFondo(tipo.tipoFondo ?? tipo.tipo ?? "Egreso");
      setMoneda((tipo.moneda === "USD" ? "USD" : "ARS"));
      setAsociarUsuario(Boolean(tipo.asociarUsuario));
      setHasChanges(false);
    } else {
      // Generate new code
      const newCodigo = `TM${String(Math.floor(Math.random() * 1000)).padStart(3, '0')}`;
      setCodigo(newCodigo);
      setNombre("");
      setDescripcion("");
      setFondo(false);
      setTipoFondo("Egreso");
      setMoneda("ARS");
      setAsociarUsuario(false);
      setHasChanges(false);
    }
  }, [tipo]);

  const handleSave = () => {
    if (!nombre.trim()) {
      toast.error("El nombre es requerido");
      return;
    }

    const saveOperation = new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // TODO: Replace with API call when backend is ready
          /*
          const response = await fetch(`${API_BASE_URL}/opciones/tipos-movimiento/${tipo?.codigo || ''}`, {
            method: tipo ? 'PUT' : 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${getAuthToken()}`
            },
            body: JSON.stringify({
              codigo,
              nombre,
              descripcion,
              fondo,
              tipoFondo,
              linkCierreDiario: fondo,
              asociarUsuario
            })
          });
          const data = await response.json();
          */

          onSave({
            ...(tipo ?? {}),
            codigo,
            nombre,
            descripcion,
            fondo,
            tipoFondo,
            moneda,
            linkCierreDiario: fondo,
            asociarUsuario,
          });
          setHasChanges(false);
          resolve({ message: tipo ? "Tipo de movimiento actualizado exitosamente" : "Tipo de movimiento creado exitosamente" });
        } catch (error) {
          reject(error);
        }
      }, 800);
    });

    toast.promise(saveOperation, {
      loading: tipo ? "Actualizando tipo de movimiento..." : "Creando tipo de movimiento...",
      success: (data: any) => data.message,
      error: "Error al guardar el tipo de movimiento",
    });
  };

  const handleDelete = () => {
    const deleteOperation = new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          // TODO: Replace with API call when backend is ready
          /*
          const response = await fetch(`${API_BASE_URL}/opciones/tipos-movimiento/${codigo}`, {
            method: 'DELETE',
            headers: {
              'Authorization': `Bearer ${getAuthToken()}`
            }
          });
          */

          onDelete(codigo);
          resolve({ message: "Tipo de movimiento eliminado exitosamente" });
        } catch (error) {
          reject(error);
        }
      }, 800);
    });

    toast.promise(deleteOperation, {
      loading: "Eliminando tipo de movimiento...",
      success: (data: any) => data.message,
      error: "Error al eliminar el tipo de movimiento",
    });

    setShowDeleteDialog(false);
  };

  const handleInputChange = () => {
    setHasChanges(true);
  };

  return (
    <div className="space-y-6">
      <div className="space-y-4">
        <div>
          <Label htmlFor="codigo">Código</Label>
          <Input
            id="codigo"
            value={codigo}
            disabled
            className="bg-muted"
          />
        </div>

        <div>
          <Label htmlFor="nombre">Nombre del Tipo de Movimiento</Label>
          <Input
            id="nombre"
            value={nombre}
            onChange={(e) => {
              setNombre(e.target.value);
              handleInputChange();
            }}
            placeholder="Ej: Adelanto de Sueldo"
          />
        </div>

        <div className="grid grid-cols-3 gap-4 items-center">
          <div>
            <Label htmlFor="fondo" className="cursor-pointer">
              Registrar en Fondos
            </Label>
            <div className="flex h-9 items-center">
              <Switch
                id="fondo"
                checked={fondo}
                onCheckedChange={(checked) => {
                  setFondo(checked);
                  handleInputChange();
                }}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="asociarUsuario" className="cursor-pointer">
              Asociar a Usuario
            </Label>
            <div className="flex h-9 items-center">
              <Switch
                id="asociarUsuario"
                checked={asociarUsuario}
                onCheckedChange={(checked) => {
                  setAsociarUsuario(checked);
                  handleInputChange();
                }}
              />
            </div>
          </div>

          <div>
            <Label htmlFor="tipoFondo">Tipo (Caja / Fondos)</Label>
            <Select
              value={tipoFondo}
              onValueChange={(value: "Ingreso" | "Egreso") => {
                const next = coerceUsdIngreso(value, moneda);
                setTipoFondo(next.tipo);
                setMoneda(next.moneda);
                handleInputChange();
              }}
            >
              <SelectTrigger id="tipoFondo" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="Ingreso">Ingreso</SelectItem>
                <SelectItem value="Egreso">Egreso</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <div>
          <Label htmlFor="monedaTipo">Moneda</Label>
          <Select
            value={moneda}
            onValueChange={(value: MonedaCaja) => {
              const next = coerceUsdIngreso(tipoFondo, value);
              setTipoFondo(next.tipo);
              setMoneda(next.moneda);
              handleInputChange();
            }}
          >
            <SelectTrigger id="monedaTipo" className="w-full">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="ARS">ARS (Pesos)</SelectItem>
              <SelectItem value="USD">USD (Dólares)</SelectItem>
            </SelectContent>
          </Select>
          {moneda === "USD" ? (
            <p className="text-xs text-muted-foreground mt-1">
              Ingreso o egreso en dólares. Entra a D/H (USD) del cierre; no mezcla efectivo ARS.
            </p>
          ) : null}
        </div>

        <div>
          <Label htmlFor="descripcion">Descripción (Opcional)</Label>
          <Textarea
            id="descripcion"
            value={descripcion}
            onChange={(e) => {
              setDescripcion(e.target.value);
              handleInputChange();
            }}
            placeholder="Descripción del tipo de movimiento..."
            rows={3}
          />
        </div>
      </div>

      <FormFooter
        onBack={onBack}
        onDelete={tipo ? () => setShowDeleteDialog(true) : undefined}
        onSave={handleSave}
        saveDisabled={!hasChanges}
      />

      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Está seguro?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acción no se puede deshacer. El tipo de movimiento será eliminado permanentemente.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} variant="destructive">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}